<template>
  <div :class="['wrapper']">
    <nav
      :class="[
        'header-with-nav',
        {
          open: menuOpen,
        },
      ]"
    >
      <div
        :class="[
          'header-with-nav__top',
          {
            scrolling: !smallscreen && y > 5,
          },
        ]"
      >
        <a
          :class="[
            'header-with-nav__top__site-btn',
            { invisible: menuOpen && smallscreen },
          ]"
          href="https://eni.com"
          :aria-label="$t('general.aria.toEni')"
        >
          <img class="" :src="`${$baseUrl}images/logo-yellow.svg`" alt="" />
        </a>

        <div v-if="!smallscreen" class="header-with-nav__right">
          <LanguageSwitcher :languages="$tm('general.languages')" />

          <SocialSharing />
        </div>

        <button v-if="smallscreen" @click="menuOpen = !menuOpen">
          <img
            class=""
            :src="`${$baseUrl}icons/${
              menuOpen ? 'close-white' : 'hamburger'
            }.svg`"
            alt=""
          />
        </button>
      </div>

      <NavBarDesktop
        v-if="!smallscreen"
        :pageLinks="pageLinks"
        :currentPage="currentPage"
      />
    </nav>

    <Transition name="fade">
      <div class="header-with-nav__menu" v-if="menuOpen">
        <div class="header-with-nav__menu__left">
          <router-link
            v-for="(page, index) in pageLinks"
            :class="[
              'header-with-nav__menu__link',
              { 'with-border': !index || index === 3 },
            ]"
            :key="page + '-' + index"
            :to="{ params: { page } }"
            @click.native="menuOpen = false"
          >
            <div v-if="index" class="header-with-nav__menu__link__pillar">
              {{ index <= 3 ? "I".repeat(index) : "" }}
              
              {{ $t("general.labels." + (index > 3 ? "extras" : "leva")) }}
            </div>
            <span>{{ $t(`navigation.${page}`) }}</span>
          </router-link>
        </div>

        <div class="header-with-nav__menu__bottom">
          <LanguageSwitcher :languages="$tm('general.languages')" />

          <SocialSharing :smallscreen="smallscreen" topModal />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";

import LanguageSwitcher from "./LanguageSwitcher.vue";
import SocialSharing from "./SocialSharing.vue";
import NavBarDesktop from "./NavBarDesktop.vue";
import { useWindowScroll } from "@vueuse/core";

defineProps({
  pageLinks: {
    type: Array,
    default: () => [],
  },
  caseStudyLinks: {
    type: Array,
    default: () => [],
  },
  languages: {
    type: Array,
    default: () => [],
  },
  currentPage: String,
  // scrollUp: Boolean,
});

const { y } = useWindowScroll();

const tvaMq = inject("$tvaMq");
const smallscreen = computed(
  () => tvaMq.value === "mobile" || tvaMq.value === "tablet"
);

const menuOpen = ref(false);
const caseStudiesOpen = ref(false);

watch(
  () => menuOpen.value,
  () => {
    document.body.style.overflow = menuOpen.value ? "hidden" : "initial";
    caseStudiesOpen.value = false;
  }
);
</script>

<style lang="scss" scoped>
.wrapper {
  --border-color: var(--white-40);
  --nav-border-color: var(--border-color);
  --text-color: var(--text-base);

  .header-with-nav {
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--z-foreground);
    width: 100%;
    color: var(--text-color);
    background: var(--background-base);

    .tablet &,
    .mobile & {
      border-bottom: 1px solid var(--border-color);
      background: var(--background-base);
    }

    &.open {
      height: 100%;
    }

    &__top {
      padding-inline: 1rem 2rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: toRem(60);
      max-height: toRem(60);
      transition: max-height 0.5s ease-in-out;

      &.scrolling {
        max-height: 0;
        overflow: hidden;
      }

      .mobile &,
      .tablet & {
        padding: 1rem;
      }

      &__site-btn {
        width: 4.1rem;
        transition: opacity 0.3s ease-in-out;

        &.invisible {
          opacity: 0;
          pointer-events: none;
        }

        img {
          width: 100%;
          display: block;
        }
      }
    }

    &-header {
      width: 100%;
    }

    &__left,
    &__right {
      display: flex;
      align-items: center;
      gap: 1.88rem;
    }

    &__left {
      gap: 1rem;

      &__button {
        width: toRem(20);
        height: toRem(20);
        // transform: translateX(toRem(-10));
        position: relative;

        & > svg {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: 100%;
          min-height: 100%;
          opacity: 0.8;
        }
        &:hover {
          & > svg {
            opacity: 1;
          }
        }
      }

      .mobile & {
        width: 100%;
        justify-content: space-between;
        flex-direction: row-reverse;
        &__back-icon {
          transform: rotate(90deg);
          &:hover {
            fill: var(--brand-stronger);
          }
        }
      }
    }

    &__menu {
      position: fixed;
      left: 0;
      top: var(--navbar-height-mobile);
      width: 100%;
      height: calc(var(--vh) * 100 - var(--navbar-height-mobile));
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      border-top: 1px solid var(--border-color);
      z-index: var(--z-foreground);
      color: var(--text-color);

      &__left {
        display: flex;
        flex-direction: column;
        padding: 4vh toRem(70);

        .mobile & {
          padding: 1.5rem;
          width: 100%;
        }
      }

      &__link {
        font-family: EniTab;
        font-size: 1.5rem;
        font-weight: 400;
        line-height: 1.5rem;
        white-space: nowrap;
        text-align: left;
        padding: min(3vh, 1.5rem) 0;

        &.with-border {
          border-bottom: 1px solid var(--border-color);
        }

        span {
          display: inline-block;

          &:first-letter {
            text-transform: uppercase;
          }
        }

        .tablet & {
          font-size: 1.25rem;
        }
        .mobile & {
          width: 100% !important;
          font-size: 1rem;
        }

        &-case-studies {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          :deep(.svg-icon) {
            transform: rotate(-90deg);
          }
        }

        .desktop &,
        .large &,
        .mobile & {
          width: toRem(205);
        }

        &__pillar {
          @include overlay-s;

          opacity: 0.8;
        }
      }

      &__bottom {
        padding: 1rem;
        display: flex;
        justify-content: space-between;
        border-top: 1px solid var(--border-color);
      }
    }
  }
}
</style>
